import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar, Users, FileCheck, Clock, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from "recharts";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { toast } from "sonner";
import { SubscriptionLimitAlert } from "@/components/SubscriptionLimitAlert";

interface ContratacaoData {
  id: string;
  created_at: string;
  stage: string;
  status_documentos: string;
  cargo: string;
  salario: number | null;
  full_name: string;
  email: string;
  org_id: string;
}

interface ChartData {
  date: string;
  total: number;
  proposta: number;
  documentos: number;
  contrato: number;
  finalizado: number;
}

interface StatusData {
  status: string;
  count: number;
  color: string;
}

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1'];

const STATUS_COLORS = {
  'proposta': '#8884d8',
  'documentos': '#82ca9d', 
  'exame': '#ffc658',
  'contrato': '#ff7300',
  'finalizado': '#8dd1e1'
};

export default function FluxoContratacoes() {
  const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
  const [contratacoes, setContratacoes] = useState<ContratacaoData[]>([]);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [statusData, setStatusData] = useState<StatusData[]>([]);
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState({
    total: 0,
    pendentes: 0,
    finalizadas: 0,
    mediaTempoFinalizacao: 0
  });
  
  const { userOrgInfo } = useOrgPermissions();

  const loadContratacoes = async () => {
    if (!userOrgInfo?.orgId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('contratacao')
        .select('*')
        .eq('org_id', userOrgInfo.orgId)
        .gte('created_at', startDate + 'T00:00:00')
        .lte('created_at', endDate + 'T23:59:59')
        .order('created_at', { ascending: true });

      if (error) throw error;

      setContratacoes(data || []);
      processChartData(data || []);
      processMetrics(data || []);
    } catch (error) {
      console.error('Erro ao carregar contratações:', error);
      toast.error('Erro ao carregar dados das contratações');
    } finally {
      setLoading(false);
    }
  };

  const processChartData = (data: ContratacaoData[]) => {
    const dateRange = eachDayOfInterval({
      start: parseISO(startDate),
      end: parseISO(endDate)
    });

    const processedData = dateRange.map(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayData = data.filter(item => 
        format(parseISO(item.created_at), 'yyyy-MM-dd') === dateStr
      );

      return {
        date: format(date, 'dd/MM', { locale: ptBR }),
        total: dayData.length,
        proposta: dayData.filter(item => item.stage === 'proposta').length,
        documentos: dayData.filter(item => item.stage === 'documentos').length,
        contrato: dayData.filter(item => item.stage === 'contrato').length,
        finalizado: dayData.filter(item => item.stage === 'finalizado').length
      };
    });

    setChartData(processedData);

    // Processar dados de status
    const statusCounts = data.reduce((acc, item) => {
      const stage = item.stage || 'proposta';
      acc[stage] = (acc[stage] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const statusDataArray = Object.entries(statusCounts).map(([status, count]) => ({
      status: status.charAt(0).toUpperCase() + status.slice(1),
      count,
      color: STATUS_COLORS[status as keyof typeof STATUS_COLORS] || '#8884d8'
    }));

    setStatusData(statusDataArray);
  };

  const processMetrics = (data: ContratacaoData[]) => {
    const total = data.length;
    const finalizadas = data.filter(item => item.stage === 'finalizado').length;
    const pendentes = total - finalizadas;

    setMetrics({
      total,
      pendentes,
      finalizadas,
      mediaTempoFinalizacao: 0 // Seria necessário calcular com base em datas de finalização
    });
  };

  useEffect(() => {
    if (userOrgInfo?.orgId) {
      loadContratacoes();
    }
  }, [userOrgInfo?.orgId, startDate, endDate]);

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Fluxo de contratações</h1>
        <Button onClick={loadContratacoes} disabled={loading}>
          <TrendingUp className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Subscription Limit Alert */}
      <SubscriptionLimitAlert />

      {/* Filtros de Período */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Filtros de Período
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="startDate">Data Início</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="endDate">Data Fim</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cards de Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Contratações</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.total}</div>
            <p className="text-xs text-muted-foreground">
              Período selecionado
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Finalizadas</CardTitle>
            <FileCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{metrics.finalizadas}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.total > 0 ? ((metrics.finalizadas / metrics.total) * 100).toFixed(1) : 0}% do total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Em Andamento</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{metrics.pendentes}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.total > 0 ? ((metrics.pendentes / metrics.total) * 100).toFixed(1) : 0}% do total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxa de Conversão</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {metrics.total > 0 ? ((metrics.finalizadas / metrics.total) * 100).toFixed(1) : 0}%
            </div>
            <p className="text-xs text-muted-foreground">
              Propostas finalizadas
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de Linha - Evolução Temporal */}
        <Card>
          <CardHeader>
            <CardTitle>Evolução das Contratações</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 5, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis 
                  dataKey="date" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  allowDecimals={false} 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--background))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="total" 
                  stroke="#8884d8" 
                  strokeWidth={2}
                  dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: '#8884d8' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Gráfico de Pizza - Status */}
        <Card>
          <CardHeader>
            <CardTitle>Distribuição por Etapa</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-4 mt-4">
              {statusData.map((entry, index) => (
                <div key={`legend-${index}`} className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-sm">
                    {entry.status}: {entry.count}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Gráfico de Barras - Contratações por Etapa ao Longo do Tempo */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Contratações por Etapa ao Longo do Tempo</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="proposta" stackId="a" fill="#8884d8" name="Proposta" />
                <Bar dataKey="documentos" stackId="a" fill="#82ca9d" name="Documentos" />
                <Bar dataKey="contrato" stackId="a" fill="#ffc658" name="Contrato" />
                <Bar dataKey="finalizado" stackId="a" fill="#ff7300" name="Finalizado" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}